import {
  Checkbox,
  Dropdown,
  Button,
  Input,
  Modal,
  Form,
  Ref,
} from 'semantic-ui-react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import SemanticDatepicker from 'react-semantic-ui-datepickers'
import TextareaAutosize from 'react-textarea-autosize'
import { startOfToday } from 'date-fns'
import { AxiosError } from 'axios'
import * as React from 'react'
import useSWR from 'swr'
import { z } from 'zod'

import {
  candidateSchema,
  positionSchema,
  companySchema,
  contactSchema,
  invoiceSchema,
  payrollSchema,
  Collection,
  userSchema,
  Task,
  User,
} from '../../types'
import {
  validateResponse,
  toUtc0Timestamp,
  currencyFormat,
  classNames,
} from '../../utils'
import { useToasts } from '../toasts/ToastsProvider'
import { useUser } from '../../pages/session/hooks'
import { useApi } from '../../store/mainContext'

import 'react-semantic-ui-datepickers/dist/react-semantic-ui-datepickers.css'

const SUBJECT_PRESETS = [
  'Send Feedback',
  'Reschedule Interview',
  'Send Challenge',
  'Prepare to Send',
  'Send to the Customer',
  'Schedule with Customer',
  'Decide what to do',
  'Follow up',
] as const

type FormValues = {
  subject: string
  details: string
  relatedCollection: Collection
  relatedId?: string
  assignedToId: User['_id']
  dueDate: Date
  requireDoneNote: boolean
}

interface Props {
  assignedToId?: User['_id']
  onSuccess(): Promise<any>
  onClose(): void
  relatedCollection?: Collection
  relatedId?: string
  task?: Task
}

export default function TaskEditorModal(props: Props) {
  const { task } = props

  const { addToast } = useToasts()
  const user = useUser()
  const api = useApi()

  const [isSaving, setIsSaving] = React.useState(false)

  const form = useForm<FormValues>({
    defaultValues: {
      subject: task?.subject || '',
      details: task?.details || '',
      relatedCollection:
        task?.relatedCollection || props.relatedCollection || 'nothing',
      relatedId: task?.relatedId || props.relatedId,
      assignedToId: task?.assignedToId || props.assignedToId || user._id,
      dueDate:
        typeof task?.dueDate === 'number'
          ? new Date(task.dueDate)
          : startOfToday(),
      requireDoneNote: Boolean(task?.requireDoneNote),
    },
  })

  const onSave: SubmitHandler<FormValues> = async (data) => {
    setIsSaving(true)
    const body: Partial<Task> = {
      subject: data.subject,
      details: data.details,
      relatedCollection: data.relatedCollection,
      requireDoneNote: data.requireDoneNote,
      assignedToId: data.assignedToId,
      dueDate: toUtc0Timestamp(data.dueDate),
      ...(data.relatedId && data.relatedCollection !== 'nothing'
        ? { relatedId: data.relatedId }
        : {}),
    }
    return (
      task ? api.patch('tasks/' + task._id, body) : api.post('tasks', body)
    )
      .catch((err: AxiosError) =>
        addToast(err.response?.data.message || err.message, {
          variant: 'danger',
        }),
      )
      .then(() => props.onSuccess())
      .finally(() => setIsSaving(false))
  }

  const relatedCollection = form.watch('relatedCollection')

  const dueDateInput = React.useRef<SemanticDatepicker>(null)

  const userCanAccessInvoices = user.hasPower('invoices')
  const userCanAccessPayrolls = user.hasPower('payrolls')

  const relatedToOptions = [
    { text: 'Nothing', value: 'nothing' },
    { text: 'a Candidate', value: 'candidates' },
    { text: 'a Contact', value: 'contacts' },
    { text: 'a Company', value: 'companies' },
    { text: 'a Position', value: 'positions' },
  ]
    .concat(
      userCanAccessInvoices
        ? { text: 'an Invoice', value: 'billing-invoices' }
        : [],
    )
    .concat(
      userCanAccessPayrolls ? [{ text: 'a Payroll', value: 'payrolls' }] : [],
    )

  const assignedToOptions = useSWR('users', () =>
    api
      .get('users')
      .then(validateResponse(z.array(userSchema)))
      .then((users) =>
        users
          .filter((u) => !u.disabled)
          .map((u) => ({
            value: u._id,
            text: user._id === u._id ? 'Me!' : u.name,
            image: { avatar: true, src: u.image },
          })),
      ),
  )

  const relatedItemsOptions = useSWR(
    relatedCollection === 'nothing'
      ? null
      : ['related-items', relatedCollection],
    () =>
      api
        .get(
          relatedCollection === 'billing-invoices'
            ? 'billing-invoices/pro'
            : relatedCollection,
          {
            params: relatedCollection === 'positions' ? { status: 'open' } : {},
          },
        )
        .then((response) => {
          if (relatedCollection === 'candidates') {
            return validateResponse(z.array(candidateSchema))(response).map(
              (candidate) => ({
                value: candidate._id,
                text: `${candidate.name} (${candidate.email})`,
              }),
            )
          }
          if (relatedCollection === 'contacts') {
            return validateResponse(z.array(contactSchema))(response).map(
              (contact) => ({
                value: contact._id,
                text: `${contact.name} (${contact.email})`,
              }),
            )
          }
          if (relatedCollection === 'companies') {
            return validateResponse(z.array(companySchema))(response).map(
              (company) => ({ value: company._id, text: company.name }),
            )
          }
          if (relatedCollection === 'positions') {
            return validateResponse(z.array(positionSchema))(response).map(
              (position) => ({
                value: position._id,
                text: `${position.number} - ${position.title}`,
              }),
            )
          }
          if (relatedCollection === 'billing-invoices') {
            return validateResponse(z.array(invoiceSchema))(response).map(
              (invoice) => ({
                value: invoice._id,
                text: `${invoice.name} (${currencyFormat(invoice.totalBilled || 0)})`,
              }),
            )
          }
          if (relatedCollection === 'payrolls') {
            return validateResponse(z.array(payrollSchema))(response).map(
              (payroll) => ({ value: payroll._id, text: payroll.name }),
            )
          }
          return []
        }),
  )

  return (
    <Modal
      closeOnDimmerClick={!isSaving}
      closeOnEscape={!isSaving}
      onClose={props.onClose}
      size="large"
      open
    >
      <Modal.Header>
        {task ? 'Edit Task' : "What's the next step?"}
      </Modal.Header>

      <Modal.Content className="!pt-0">
        <Form onSubmit={form.handleSubmit(onSave)} spellCheck="false">
          <label className="text-lg font-normal leading-none">
            Quick Actions:
          </label>
          <div className="mb-6 mt-4">
            <Button.Group
              buttons={SUBJECT_PRESETS.map((content) => ({
                onClick: () => form.setValue('subject', content),
                content,
                disabled: isSaving,
                key: content,
                type: 'button',
                className: '!shrink',
              }))}
              basic
              fluid
            />
          </div>

          <Controller
            name="subject"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => {
              return (
                <Form.Field>
                  <label className="!text-lg !font-normal leading-none">
                    Subject *
                  </label>
                  <Input
                    {...field}
                    onChange={(_, { value }) => field.onChange(value)}
                    fluid
                    error={!!fieldState.error}
                    disabled={isSaving}
                    input={<input data-1p-ignore autoFocus />}
                  />
                  <div className="text-base leading-normal text-red-600 empty:hidden">
                    {fieldState.error?.message}
                  </div>
                  <div className="mt-1 text-base leading-normal text-slate-500">
                    Specify an action, something to do.
                  </div>
                </Form.Field>
              )
            }}
          />

          <Controller
            name="details"
            control={form.control}
            render={({ field, fieldState }) => {
              return (
                <Form.Field>
                  <label className="!text-lg !font-normal leading-none">
                    Details
                  </label>
                  <TextareaAutosize
                    {...form.register('details')}
                    className={classNames(
                      'field !mb-0 !resize-none !text-lg',
                      isSaving && 'disabled',
                      form.formState.errors.details && 'error',
                    )}
                    disabled={isSaving}
                    minRows={3}
                    rows={3}
                  />
                  <div className="text-base leading-normal text-red-600 empty:hidden">
                    {fieldState.error?.message}
                  </div>
                </Form.Field>
              )
            }}
          />

          <Controller
            name="relatedCollection"
            control={form.control}
            rules={{ required: 'Required' }}
            render={({ field, fieldState }) => (
              <div className="text-lg">
                <label className="mr-1.5 !text-inherit">Related To</label>
                <Ref innerRef={field.ref}>
                  <Dropdown
                    {...field}
                    className="!text-inherit"
                    selectOnBlur={false}
                    disabled={isSaving}
                    onChange={(_, { value }) => {
                      form.setValue('relatedId', undefined)
                      field.onChange(value)
                    }}
                    options={relatedToOptions}
                    error={!!fieldState.error}
                    inline
                  />
                </Ref>
                <div className="text-base leading-normal text-red-600 empty:hidden">
                  {fieldState.error?.message}
                </div>
              </div>
            )}
          />

          <Controller
            name="relatedId"
            control={form.control}
            rules={{
              required: relatedCollection === 'nothing' ? false : 'Required',
            }}
            render={({ field, fieldState }) => (
              <div
                className={classNames(
                  relatedCollection === 'nothing' && 'hidden',
                )}
              >
                <Ref innerRef={field.ref}>
                  <Dropdown
                    {...field}
                    selectOnBlur={false}
                    placeholder={
                      relatedItemsOptions.isLoading
                        ? 'Loading...'
                        : `Select ${
                            relatedToOptions.find(
                              (o) => o.value === relatedCollection,
                            )?.text
                          }...`
                    }
                    disabled={
                      (!userCanAccessPayrolls &&
                        relatedCollection === 'payrolls') ||
                      (!userCanAccessInvoices &&
                        relatedCollection === 'billing-invoices') ||
                      relatedItemsOptions.isLoading ||
                      isSaving
                    }
                    loading={relatedItemsOptions.isLoading}
                    onChange={(_, { value }) => field.onChange(value)}
                    options={relatedItemsOptions.data || []}
                    error={!!fieldState.error}
                    selection
                    search
                    fluid
                  />
                </Ref>
                <div className="text-base leading-normal text-red-600">
                  {fieldState.error?.message}
                </div>
              </div>
            )}
          />

          <div className="mt-6 grid grid-cols-3 gap-6">
            <div>
              <label className="mr-1.5 !text-lg">Assigned To *</label>
              <Controller
                name="assignedToId"
                control={form.control}
                rules={{ required: 'Required' }}
                render={({ field, fieldState }) => (
                  <div>
                    <Ref innerRef={field.ref}>
                      <Dropdown
                        {...field}
                        selectOnBlur={false}
                        placeholder="Select a user..."
                        disabled={assignedToOptions.isLoading || isSaving}
                        loading={assignedToOptions.isLoading}
                        onChange={(_, { value }) => field.onChange(value)}
                        options={assignedToOptions.data || []}
                        error={!!fieldState.error}
                        selection
                        search
                        fluid
                        clearable
                      />
                    </Ref>
                    <div className="text-base leading-normal text-red-600 empty:hidden">
                      {fieldState.error?.message}
                    </div>
                  </div>
                )}
              />
            </div>
            <div>
              <Controller
                name="dueDate"
                control={form.control}
                rules={{ required: 'Required' }}
                render={({ field, fieldState }) => (
                  <div className="[&_.field]:!mb-0 [&_.field]:!w-full">
                    <label htmlFor={field.name} className="block !text-lg">
                      Due Date *
                    </label>
                    <SemanticDatepicker
                      {...field}
                      onChange={(_, { value }) => field.onChange(value)}
                      clearOnSameDateClick={false} // required or would send `null` when clicking same day
                      clearable
                      showToday={false}
                      datePickerOnly
                      disabled={field.disabled || isSaving}
                      error={!!fieldState.error}
                      ref={dueDateInput}
                      pointing="top left"
                    />
                    <div className="text-base leading-normal text-red-600 empty:hidden">
                      {fieldState.error?.message}
                    </div>
                  </div>
                )}
              />
            </div>
            <div className="pt-9">
              <Controller
                name="requireDoneNote"
                control={form.control}
                render={({ field: { value, onChange, ref, ...other } }) => (
                  <label
                    className={classNames(
                      'flex gap-2',
                      !isSaving && 'cursor-pointer',
                    )}
                  >
                    <Ref innerRef={ref}>
                      <Checkbox
                        {...other}
                        disabled={isSaving}
                        checked={!!value}
                        onChange={(_, p) => onChange(p.checked)}
                      />
                    </Ref>
                    <div className="text-lg leading-snug">
                      Requires fulfillment note
                    </div>
                  </label>
                )}
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end gap-2">
            <Button
              onClick={props.onClose}
              content="Nevermind..."
              type="button"
              className="mr-0"
              disabled={isSaving}
              basic
            />
            <Button
              loading={isSaving}
              disabled={isSaving}
              content="Save Changes!"
              icon="save"
              primary
              type="submit"
              className="mr-0"
            />
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
